// Title cleanup for upstream public-hearing rows.
//
// Upstream titles follow a consistent City-of-Calgary pattern that
// duplicates information we already render as structured chips:
//
//   "Land Use Amendment in Beltline (Ward 8) at 310 – 15 Avenue SW,
//    LOC2025-0242, CPC2026-0258"
//
// `(Ward 8)` is the ward chip; `LOC2025-0242` is the LOC chip; CPC is
// not surfaced anywhere on the row. Stripping them leaves the part a
// reader is actually trying to scan: the action verb, the community,
// and the address.

const WARD_PARENS_RE = /\s*\(Ward\s+\d+\)\s*/gi;
const TRAILING_CASE_IDS_RE = /(?:\s*,\s*(?:LOC|CPC)\d{4}-\d{4})+\s*$/i;
const COMMA_BEFORE_AT_RE = /\s*,\s+(?=at\s)/gi;
const MULTIPLE_SPACES_RE = /\s{2,}/g;

export function formatHearingTitle(raw: string): string {
  const cleaned = raw
    .replace(TRAILING_CASE_IDS_RE, "")
    .replace(WARD_PARENS_RE, " ")
    .replace(COMMA_BEFORE_AT_RE, " ")
    .replace(MULTIPLE_SPACES_RE, " ")
    .trim()
    .replace(/[\s,;.]+$/g, "");

  return cleaned.length > 0 ? cleaned : raw;
}

export type TitleSegment = { text: string; bold: boolean };

export type TitleParts = {
  headline: TitleSegment[];
  address: string | null;
};

// Build community-bolded segments from a single string. Communities are
// matched case-insensitively as whole-token substrings; longer names are
// matched first so "East Village" wins over any shorter overlap.
function boldCommunities(
  text: string,
  communities: readonly string[],
): TitleSegment[] {
  const names = communities
    .filter((c) => c.length > 0)
    .slice()
    .sort((a, b) => b.length - a.length);

  if (names.length === 0) return [{ text, bold: false }];

  const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const pattern = new RegExp(
    `\\b(${names.map(escape).join("|")})\\b`,
    "gi",
  );

  const segments: TitleSegment[] = [];
  let lastIndex = 0;
  for (const match of text.matchAll(pattern)) {
    const start = match.index ?? 0;
    if (start > lastIndex) {
      segments.push({ text: text.slice(lastIndex, start), bold: false });
    }
    segments.push({ text: match[0], bold: true });
    lastIndex = start + match[0].length;
  }
  if (lastIndex < text.length) {
    segments.push({ text: text.slice(lastIndex), bold: false });
  }

  return segments.length > 0 ? segments : [{ text, bold: false }];
}

// Split a cleaned title into a headline (action + community) and an
// optional address suffix. The City pattern places the address after
// " at " (e.g. "Land Use Amendment in Mission at 111 — 17 Avenue SW");
// when no " at " is present, the entire cleaned title is the headline.
export function splitHearingTitle(
  raw: string,
  communities: readonly string[],
): TitleParts {
  const cleaned = formatHearingTitle(raw);
  const atMatch = cleaned.match(/\s+at\s+/i);

  let headlineText = cleaned;
  let address: string | null = null;
  if (atMatch && atMatch.index !== undefined) {
    headlineText = cleaned.slice(0, atMatch.index);
    address = cleaned.slice(atMatch.index + atMatch[0].length).trim() || null;
  }

  return {
    headline: boldCommunities(headlineText, communities),
    address,
  };
}
